import { useState } from 'react'
import { ErrorBanner } from '@/components/AppLayout'
import { resetEvent, type ResetEventResult } from '@/lib/queries'

interface Props {
  eventId: string
  eventName: string
  isOwner: boolean
  onReset: () => void
}

type Level = 'submissions' | 'everything'

/**
 * Undoing an event.
 *
 * Two levels, because they are different decisions. Clearing a rehearsal's
 * submissions is routine; deleting the attendee list throws away an import or
 * an evening of typing. Both ask for the event name first — a confirm dialog is
 * too easy to dismiss for something with no undo.
 */
export function ResetEventCard({ eventId, eventName, isOwner, onReset }: Props) {
  const [level, setLevel] = useState<Level | null>(null)
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<ResetEventResult | null>(null)

  const confirmed = typed.trim() === eventName.trim()

  async function handleReset() {
    if (level === null || !confirmed) return

    setBusy(true)
    setError(null)
    try {
      const result = await resetEvent(eventId, level === 'everything')
      setDone(result)
      setLevel(null)
      setTyped('')
      onReset()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reset this event')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card card-danger">
      <h2>Reset this event</h2>

      {error !== null && <ErrorBanner message={error} />}

      {done !== null && (
        <p className="banner banner-success">
          Removed {done.submissions_removed}{' '}
          {done.submissions_removed === 1 ? 'submission' : 'submissions'}
          {done.attendees_removed > 0 && ` and ${String(done.attendees_removed)} attendees`}. The
          form is back to draft and needs a new passcode before it can be published again.
        </p>
      )}

      {!isOwner ? (
        <p className="field-hint">Only an organization owner can reset an event.</p>
      ) : level === null ? (
        <>
          <p className="field-hint">
            Both options clear every submission and any matchmaking results, return the form to
            draft, and clear its passcode — anyone part-way through the form is signed out. Neither
            can be undone.
          </p>
          <div className="row btn-group-stack">
            <button
              className="btn"
              type="button"
              onClick={() => {
                setLevel('submissions')
                setDone(null)
              }}
            >
              Clear submissions and results
            </button>
            <button
              className="btn btn-danger"
              type="button"
              onClick={() => {
                setLevel('everything')
                setDone(null)
              }}
            >
              Remove all attendees too
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="banner banner-danger">
            {level === 'everything' ? (
              <>
                This deletes <strong>every attendee</strong> for this event, along with their
                submissions and matches. You will need to import or add them again.
              </>
            ) : (
              <>
                This deletes every submission and matchmaking result for this event. Attendees and
                their nametag numbers are kept.
              </>
            )}
          </p>

          <div className="field">
            <label className="field-label" htmlFor="confirm-name">
              Type <strong>{eventName}</strong> to confirm
            </label>
            <input
              id="confirm-name"
              type="text"
              value={typed}
              onChange={(e) => {
                setTyped(e.target.value)
              }}
              autoComplete="off"
              autoFocus
            />
          </div>

          <div className="row btn-group-stack">
            <button
              className="btn btn-danger"
              type="button"
              disabled={busy || !confirmed}
              onClick={() => void handleReset()}
            >
              {busy
                ? 'Resetting…'
                : level === 'everything'
                  ? 'Remove everything'
                  : 'Clear submissions'}
            </button>
            <button
              className="btn"
              type="button"
              disabled={busy}
              onClick={() => {
                setLevel(null)
                setTyped('')
              }}
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  )
}
